// react_app/src/components/admin/translations/TranslationTable.tsx

import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../i18n/useTranslation';
import { TranslationRow } from './TranslationRow';
import type { LanguageTextDto } from '../../../api/translationService';

interface TranslationTableProps {
  texts: LanguageTextDto[];
  cultureName: string;
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onTextUpdated: (name: string, newValue: string) => void;
}

export function TranslationTable({
  texts,
  cultureName,
  loading,
  page,
  totalPages,
  onPageChange,
  onTextUpdated,
}: TranslationTableProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        <span className="ml-2 text-neutral-500">{t('App.Admin.Translations.Loading')}</span>
      </div>
    );
  }

  if (texts.length === 0) {
    return (
      <div className="text-center py-20 text-neutral-500">
        {t('App.Admin.Translations.NoResults')}
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left">
              <th className="px-4 py-3 font-medium text-neutral-600">{t('App.Admin.Translations.ColumnKey')}</th>
              <th className="px-4 py-3 font-medium text-neutral-600">{t('App.Admin.Translations.ColumnBase')}</th>
              <th className="px-4 py-3 font-medium text-neutral-600">{t('App.Admin.Translations.ColumnTranslation')}</th>
              <th className="px-4 py-3 font-medium text-neutral-600 w-10">{t('App.Admin.Translations.ColumnStatus')}</th>
              <th className="px-4 py-3 font-medium text-neutral-600 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {texts.map((text) => (
              <TranslationRow
                key={text.name}
                text={text}
                cultureName={cultureName}
                onUpdated={onTextUpdated}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200">
          <span className="text-sm text-neutral-500">
            {t('App.Admin.Translations.PageInfo', { page, totalPages })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="p-2 rounded-md hover:bg-neutral-100 disabled:opacity-30 min-h-[44px] min-w-[44px]"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
              className="p-2 rounded-md hover:bg-neutral-100 disabled:opacity-30 min-h-[44px] min-w-[44px]"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
